import { CartDiscountUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Flash sale. Reads the shop metafield mirror embedded by snippets/st-flash-sale-script.liquid and
 * reveals sale prices and the section only while the window is open. Keeps the cart's FLASH- codes
 * in step with the cart. Cached pages can't know the time, so this runs on load, after cart changes,
 * and at each window boundary.
 */

const CODE_PREFIX = 'FLASH-';
// setTimeout overflows past 2^31 - 1 ms, so long waits are capped and rescheduled when they fire.
const MAX_DELAY = 2147483647;

let syncing = false;
let syncQueued = false;

function readSale() {
  try {
    return JSON.parse(document.getElementById('st-flash-sale-data')?.textContent ?? 'null');
  } catch {
    return null;
  }
}

/**
 * @param {ReturnType<typeof readSale>} sale
 * @returns {{ productId: number, code: string, soldOut: boolean }[]}
 */
function liveProducts(sale) {
  const now = Date.now();
  if (!sale || now < Date.parse(sale.startsAt) || now >= Date.parse(sale.endsAt)) return [];
  return sale.products.filter((product) => !product.soldOut);
}

function renderSale() {
  const live = liveProducts(readSale());
  const liveIds = new Set(live.map((product) => String(product.productId)));

  for (const container of document.querySelectorAll('[data-st-flash-product-id]')) {
    const isLive = liveIds.has(container.dataset.stFlashProductId);
    for (const child of container.children) {
      child.hidden = child.classList.contains('st-flash-price') ? !isLive : isLive;
    }
  }

  for (const section of document.querySelectorAll('[data-st-flash-section]')) {
    section.hidden = live.length === 0;
  }

  // Buy now skips the cart, so it would never get the FLASH- code: hide it while the product is on sale.
  for (const form of document.querySelectorAll('product-form-component[data-product-id]')) {
    form.toggleAttribute('data-st-flash-live', liveIds.has(form.dataset.productId ?? ''));
  }
}

async function syncCart() {
  if (syncing) {
    syncQueued = true;
    return;
  }
  syncing = true;

  try {
    const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
    const inCart = new Set(cart.items.map((item) => item.product_id));
    const wanted = liveProducts(readSale())
      .filter((product) => inCart.has(product.productId))
      .map((product) => product.code);
    const current = cart.discount_codes.map((discount) => discount.code);
    // Customer codes are kept as they are; only our FLASH- codes come and go.
    const keep = [...new Set([...current.filter((code) => !code.startsWith(CODE_PREFIX)), ...wanted])];

    if ([...keep].sort().join() === [...current].sort().join()) return;

    // Dispatched from outside the cart sections, so cart-items treats it as external and re-renders totals.
    const deferred = CartDiscountUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: keep.map((code) => ({ code })),
        promise: deferred.promise,
      }),
    );
    try {
      const response = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', {
          body: JSON.stringify({ discount: keep.join(',') }),
        }),
      );
      deferred.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(await response.json()),
      });
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  } catch (error) {
    console.warn('[st-flash-sale] Could not sync discount codes', error);
  } finally {
    syncing = false;
    if (syncQueued) {
      syncQueued = false;
      syncCart();
    }
  }
}

function scheduleBoundary() {
  const sale = readSale();
  if (!sale) return;

  const now = Date.now();
  const upcoming = [Date.parse(sale.startsAt), Date.parse(sale.endsAt)].filter((time) => time > now);
  if (upcoming.length === 0) return;

  setTimeout(onBoundary, Math.min(Math.min(...upcoming) - now, MAX_DELAY));
}

function onBoundary() {
  renderSale();
  syncCart();
  scheduleBoundary();
}

renderSale();
syncCart();
scheduleBoundary();

// Section rendering morphs price markup back to Liquid's default state, so re-apply after DOM changes.
let frame = 0;
new MutationObserver(() => {
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(renderSale);
}).observe(document.body, { childList: true, subtree: true });

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise?.then(syncCart, syncCart);
});
